/**
 * "Chrome" del editor in-app (mock Figma 2026-09): dos barras FIJAS arriba de la
 * app del cliente, dibujadas por el SDK (sin iframe):
 *
 *   1. Topbar oscura: logo · «Nombre · fecha» · app · [barra arriba/abajo]
 *      [mostrar editor] [vista previa] · Salir · Guardar ▾ (Guardar y salir).
 *   Vista previa: se esconden barras y tarjeta y queda una píldora
 *   «Estás en vista previa | Salir» para volver al editor.
 *   2. Fila de pasos: tarjeta «Activación» + una tarjeta por paso + «Agregar
 *      paso», con la píldora «Ocultar/Mostrar pasos» colgando al centro.
 *
 * La app se EMPUJA (`margin-top`/`margin-bottom` en <html>, según dónde esté
 * la barra) para que las barras no tapen nada. El chrome NO tiene estado propio: refleja el `EditorChromeState`
 * que publica el panel y devuelve `ChromeAction`s; el panel decide.
 */
import { hasDocument, hasWindow } from '../../utils/safe-env';
import type { ChromeAction, EditorChromeState } from './bridge-protocol';

export const CHROME_TOPBAR_H = 64;
export const CHROME_STEPS_H = 128;

export interface BuilderChromeOptions {
  /** Nombre de la app (origen) que se muestra junto al nombre de la guía. */
  appLabel: string;
  onAction: (action: ChromeAction) => void;
}

export type ChromeDock = 'top' | 'bottom';

export interface BuilderChromeHandle {
  /** Refresca las barras con el estado publicado por el panel. */
  setState(state: EditorChromeState): void;
  /** Alto total ocupado hoy (topbar + pasos si están visibles). */
  height(): number;
  /** Dónde está anclada la barra. */
  dock(): ChromeDock;
  /** Modo vista previa: oculta las barras y muestra la píldora para salir. */
  setPreview(on: boolean): void;
  /** Oculta las barras SIN píldora (mientras dura el picker de elementos). */
  setHidden(on: boolean): void;
  /** Se llama cuando cambia el alto (colapsar/expandir pasos). */
  onHeightChange(cb: (h: number) => void): void;
  teardown(): void;
}

const ICONS = {
  bolt: '<path stroke-linecap="round" stroke-linejoin="round" d="m3.75 13.5 10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75Z"/>',
  desktop:
    '<path stroke-linecap="round" stroke-linejoin="round" d="M9 17.25v1.007a3 3 0 0 1-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0 1 15 18.257V17.25m6-12V15a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 15V5.25m18 0A2.25 2.25 0 0 0 18.75 3H5.25A2.25 2.25 0 0 0 3 5.25m18 0V12a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 12V5.25"/>',
  updown:
    '<path stroke-linecap="round" stroke-linejoin="round" d="M3 7.5 7.5 3m0 0L12 7.5M7.5 3v13.5m13.5 0L16.5 21m0 0L12 16.5m4.5 4.5V7.5"/>',
  map: '<path stroke-linecap="round" stroke-linejoin="round" d="M9 6.75V15m6-6v8.25m.503 3.498 4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 0 0-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0Z"/>',
  eye: '<path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"/><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"/>',
  chevronDown:
    '<path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5"/>',
  chevronUp:
    '<path stroke-linecap="round" stroke-linejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5"/>',
  plus: '<path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>',
  cursor:
    '<path stroke-linecap="round" stroke-linejoin="round" d="M15.042 21.672 13.684 16.6m0 0-2.51 2.225.569-9.47 5.227 7.917-3.286-.672ZM12 2.25V4.5m5.834.166-1.591 1.591M20.25 10.5H18M7.757 14.743l-1.59 1.59M6 10.5H3.75m4.007-4.243-1.59-1.59"/>',
  spinner:
    '<path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182"/>',
};

function svg(name: keyof typeof ICONS, size = 20): string {
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" ` +
    `fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true">${ICONS[name]}</svg>`
  );
}

/** «19 ago» (fecha corta, locale es). */
function shortDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('es', { day: 'numeric', month: 'short' }).replace('.', '');
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  html?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (html !== undefined) node.innerHTML = html;
  return node;
}

function iconButton(name: keyof typeof ICONS, title: string, className = ''): HTMLButtonElement {
  const b = el('button', `veo-tb-icon ${className}`.trim(), svg(name));
  b.type = 'button';
  b.title = title;
  b.setAttribute('aria-label', title);
  return b;
}

/** Inyecta el chrome del editor. Devuelve el handle (o uno inerte sin DOM). */
export function injectBuilderChrome(opts: BuilderChromeOptions): BuilderChromeHandle {
  if (!hasWindow() || !hasDocument()) {
    return {
      setState: () => {},
      height: () => 0,
      dock: () => 'top',
      setPreview: () => {},
      setHidden: () => {},
      onHeightChange: () => {},
      teardown: () => {},
    };
  }

  const host = document.createElement('div');
  host.setAttribute('data-veo-builder-chrome', '');
  host.setAttribute('data-veo-ignore', '');
  const shadow = host.attachShadow({ mode: 'open' });
  const style = document.createElement('style');
  style.textContent = CHROME_CSS;

  const root = el('div', 'veo-chrome');

  // ── Topbar ──
  const topbar = el('div', 'veo-tb');
  const brand = el('div', 'veo-tb-brand', svg('bolt', 18));
  const nameEl = el('div', 'veo-tb-name');
  const statusEl = el('span', 'veo-tb-status');
  statusEl.hidden = true;
  const sep = el('span', 'veo-tb-sep');
  const appEl = el('div', 'veo-tb-app', `${svg('desktop', 16)}<span></span>`);
  (appEl.querySelector('span') as HTMLElement).textContent = opts.appLabel;
  const left = el('div', 'veo-tb-left');
  left.append(brand, nameEl, statusEl, sep, appEl);

  const right = el('div', 'veo-tb-right');
  const dockBtn = iconButton('updown', 'Mover la barra abajo');
  const showPanel = iconButton('map', 'Mostrar el editor');
  const preview = iconButton('eye', 'Vista previa');
  const exit = el('button', 'veo-tb-text', 'Salir');
  exit.type = 'button';
  const saveGroup = el('div', 'veo-tb-save');
  const save = el('button', 'veo-tb-save-main', '<span>Guardar</span>');
  save.type = 'button';
  const saveMore = el('button', 'veo-tb-save-more', svg('chevronDown', 16));
  saveMore.type = 'button';
  saveMore.title = 'Más opciones de guardado';
  saveMore.setAttribute('aria-label', 'Más opciones de guardado');
  const saveMenu = el('div', 'veo-tb-menu');
  const saveExit = el('button', 'veo-tb-menu-item', 'Guardar y salir');
  saveExit.type = 'button';
  saveMenu.append(saveExit);
  saveMenu.hidden = true;
  saveGroup.append(save, saveMore, saveMenu);
  right.append(dockBtn, showPanel, preview, el('span', 'veo-tb-vsep'), exit, saveGroup);
  topbar.append(left, right);

  // ── Fila de pasos ──
  const stepsBar = el('div', 'veo-steps');
  const stepsList = el('div', 'veo-steps-list');
  stepsBar.append(stepsList);

  const togglePill = el('button', 'veo-steps-toggle');
  togglePill.type = 'button';

  root.append(topbar, stepsBar, togglePill);

  // Píldora de vista previa (solo visible en ese modo).
  const previewPill = el('div', 'veo-preview-pill');
  const previewLabel = el(
    'span',
    'veo-preview-label',
    `${svg('eye', 18)}<span>Estás en vista previa</span>`,
  );
  const previewExit = el('button', 'veo-preview-exit', 'Salir');
  previewExit.type = 'button';
  previewPill.append(previewLabel, previewExit);
  previewPill.hidden = true;

  shadow.append(style, root, previewPill);
  document.body.appendChild(host);

  // ── Empujar la app (arriba o abajo según el anclaje) ──
  const html = document.documentElement;
  const prevMarginTop = html.style.marginTop;
  const prevMarginBottom = html.style.marginBottom;
  let collapsed = false;
  let dock: ChromeDock = 'top';
  let previewOn = false;
  let hiddenOn = false;
  let heightCb: ((h: number) => void) | null = null;
  const height = (): number => CHROME_TOPBAR_H + (collapsed ? 0 : CHROME_STEPS_H);
  const applyHeight = (): void => {
    const h = previewOn || hiddenOn ? 0 : height();
    html.style.marginTop = dock === 'top' ? `${h}px` : prevMarginTop;
    html.style.marginBottom = dock === 'bottom' ? `${h}px` : prevMarginBottom;
    root.style.setProperty('--veo-chrome-h', `${height()}px`);
    root.classList.toggle('is-bottom', dock === 'bottom');
    stepsBar.hidden = collapsed;
    const open = dock === 'top' ? 'chevronUp' : 'chevronDown';
    const closedIcon = dock === 'top' ? 'chevronDown' : 'chevronUp';
    togglePill.innerHTML = `<span>${collapsed ? 'Mostrar pasos' : 'Ocultar pasos'}</span>${svg(
      collapsed ? closedIcon : open,
      16,
    )}`;
    dockBtn.title = dock === 'top' ? 'Mover la barra abajo' : 'Mover la barra arriba';
    dockBtn.setAttribute('aria-label', dockBtn.title);
    heightCb?.(h);
  };
  const setPreview = (on: boolean): void => {
    previewOn = on;
    root.hidden = on || hiddenOn;
    previewPill.hidden = !on;
    applyHeight();
  };
  const setHidden = (on: boolean): void => {
    hiddenOn = on;
    root.hidden = on || previewOn;
    applyHeight();
  };
  togglePill.addEventListener('click', () => {
    collapsed = !collapsed;
    applyHeight();
  });

  // ── Acciones ──
  const act = (a: ChromeAction): void => opts.onAction(a);
  dockBtn.addEventListener('click', () => {
    dock = dock === 'top' ? 'bottom' : 'top';
    applyHeight();
    act({ action: 'dock', position: dock });
  });
  showPanel.addEventListener('click', () => act({ action: 'show-panel' }));
  preview.addEventListener('click', () => act({ action: 'preview' }));
  previewExit.addEventListener('click', () => act({ action: 'exit-preview' }));
  exit.addEventListener('click', () => act({ action: 'exit' }));
  save.addEventListener('click', () => act({ action: 'save', exit: false }));
  saveMore.addEventListener('click', (e) => {
    e.stopPropagation();
    saveMenu.hidden = !saveMenu.hidden;
  });
  saveExit.addEventListener('click', () => {
    saveMenu.hidden = true;
    act({ action: 'save', exit: true });
  });
  const closeMenu = (): void => {
    saveMenu.hidden = true;
  };
  document.addEventListener('click', closeMenu);

  // ── Render de estado ──
  let current: EditorChromeState | null = null;
  const render = (): void => {
    const s = current;
    if (!s) return;
    const date = shortDate(s.createdAt);
    nameEl.textContent = `${s.guideName.trim() || 'Sin nombre'}${date ? ` · ${date}` : ''}`;
    if (s.status) {
      statusEl.hidden = false;
      statusEl.textContent = s.status.label;
      statusEl.dataset.tone = s.status.tone;
      statusEl.title = 'Estado de la guía (se cambia desde la lista de guías)';
    } else {
      statusEl.hidden = true;
    }
    save.innerHTML = s.saving
      ? `${svg('spinner', 16)}<span>Guardando…</span>`
      : '<span>Guardar</span>';
    save.disabled = s.saving;
    saveExit.disabled = s.saving;

    stepsList.replaceChildren();
    const activation = stepCard(svg('cursor', 22), 'Activación', s.section === 'activation');
    activation.addEventListener('click', () => act({ action: 'select-activation' }));
    stepsList.append(activation);
    s.steps.forEach((st, i) => {
      const thumb =
        st.presentation === 'inline'
          ? '<div class="veo-thumb veo-thumb-inline"><i></i><i></i><i></i></div>'
          : '<div class="veo-thumb"><i></i><i></i><b></b></div>';
      const card = stepCard(thumb, st.label, s.section === 'step' && s.activeStep === i);
      card.addEventListener('click', () => act({ action: 'select-step', index: i }));
      stepsList.append(card);
    });
    const add = el('button', 'veo-step-add', `${svg('plus', 18)}<span>Agregar paso</span>`);
    add.type = 'button';
    add.addEventListener('click', () => act({ action: 'add-step' }));
    stepsList.append(add);
  };

  applyHeight();

  return {
    setState: (state) => {
      current = state;
      render();
    },
    height,
    dock: () => dock,
    setPreview,
    setHidden,
    onHeightChange: (cb) => {
      heightCb = cb;
    },
    teardown: () => {
      document.removeEventListener('click', closeMenu);
      html.style.marginTop = prevMarginTop;
      html.style.marginBottom = prevMarginBottom;
      host.remove();
    },
  };
}

function stepCard(inner: string, label: string, active: boolean): HTMLButtonElement {
  const b = el(
    'button',
    `veo-step${active ? ' is-active' : ''}`,
    `<div class="veo-step-box">${inner}</div><span class="veo-step-label"></span>`,
  );
  b.type = 'button';
  (b.querySelector('.veo-step-label') as HTMLElement).textContent = label;
  b.setAttribute('aria-pressed', active ? 'true' : 'false');
  return b;
}

const CHROME_CSS = `
:host { all: initial; }
.veo-chrome {
  position: fixed; top: 0; left: 0; right: 0;
  display: flex; flex-direction: column;
  z-index: 2147483646;
  font-family: system-ui, -apple-system, "Segoe UI", sans-serif;
  font-size: 14px; color: #1c1917;
}
.veo-chrome[hidden] { display: none; }
.veo-chrome.is-bottom { top: auto; bottom: 0; flex-direction: column-reverse; }
.veo-chrome.is-bottom .veo-tb-menu { top: auto; bottom: calc(100% + 8px); }
.veo-chrome.is-bottom .veo-steps { border-bottom: 0; border-top: 1px solid #d6cfc8; }
.veo-chrome.is-bottom .veo-steps-toggle { top: auto; bottom: var(--veo-chrome-h, ${CHROME_TOPBAR_H + CHROME_STEPS_H}px); transform: translate(-50%, 50%); }
.veo-preview-pill {
  position: fixed; top: 28px; left: 50%; transform: translateX(-50%);
  z-index: 2147483646;
  display: inline-flex; align-items: center; gap: 12px;
  background: #2b2623; color: #fff; border-radius: 999px; padding: 12px 20px;
  font-family: system-ui, -apple-system, "Segoe UI", sans-serif; font-size: 15px; font-weight: 500;
  box-shadow: 0 8px 24px rgba(0,0,0,0.3);
}
.veo-preview-pill[hidden] { display: none; }
.veo-preview-label { display: inline-flex; align-items: center; gap: 10px; padding-right: 12px; border-right: 1px solid rgba(255,255,255,0.3); }
.veo-preview-exit { border: 0; background: transparent; color: #fff; font: inherit; cursor: pointer; padding: 0 2px; }
.veo-preview-exit:hover { text-decoration: underline; }
.veo-tb {
  height: ${CHROME_TOPBAR_H}px; box-sizing: border-box;
  display: flex; align-items: center; justify-content: space-between;
  padding: 0 20px; gap: 16px;
  background: #2b2623; color: #fff;
}
.veo-tb-left, .veo-tb-right { display: flex; align-items: center; gap: 12px; min-width: 0; }
.veo-tb-brand {
  width: 36px; height: 36px; border-radius: 999px; background: #b74d00;
  display: inline-flex; align-items: center; justify-content: center; flex: 0 0 auto;
}
.veo-tb-name { font-weight: 700; font-size: 16px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.veo-tb-status { display: inline-flex; align-items: center; padding: 2px 8px; border-radius: 999px; font-size: 11px; font-weight: 600; letter-spacing: 0.02em; white-space: nowrap; background: rgba(255,255,255,0.18); color: #fff; }
.veo-tb-status[data-tone="live"] { background: #16a34a; }
.veo-tb-status[data-tone="testing"] { background: #b45309; }
.veo-tb-status[data-tone="review"] { background: #7c3aed; }
.veo-tb-status[data-tone="draft"] { background: #0f766e; }
.veo-tb-status[data-tone="paused"] { background: rgba(255,255,255,0.28); }
.veo-tb-sep { width: 1px; height: 24px; background: rgba(255,255,255,0.25); }
.veo-tb-vsep { width: 1px; height: 28px; background: rgba(255,255,255,0.25); margin: 0 4px; }
.veo-tb-app { display: inline-flex; align-items: center; gap: 8px; color: rgba(255,255,255,0.85); white-space: nowrap; }
.veo-tb-icon {
  width: 42px; height: 42px; border-radius: 10px; border: 0;
  background: rgba(255,255,255,0.12); color: #fff; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
}
.veo-tb-icon:hover { background: rgba(255,255,255,0.22); }
.veo-tb-text { border: 0; background: transparent; color: #fff; font: inherit; font-size: 15px; cursor: pointer; padding: 8px 6px; }
.veo-tb-text:hover { text-decoration: underline; }
.veo-tb-save { position: relative; display: inline-flex; border-radius: 10px; overflow: visible; }
.veo-tb-save-main, .veo-tb-save-more {
  border: 0; background: #b74d00; color: #fff; font: inherit; font-weight: 600; font-size: 15px;
  cursor: pointer; display: inline-flex; align-items: center; gap: 8px; height: 42px;
}
.veo-tb-save-main { padding: 0 8px 0 18px; border-radius: 10px 0 0 10px; }
.veo-tb-save-more { padding: 0 12px 0 6px; border-radius: 0 10px 10px 0; }
.veo-tb-save-main:hover, .veo-tb-save-more:hover { background: #953c00; }
.veo-tb-save-main:disabled { opacity: 0.7; cursor: default; }
.veo-tb-menu {
  position: absolute; right: 0; top: calc(100% + 8px); min-width: 220px;
  background: #fff; color: #1c1917; border-radius: 12px; padding: 6px;
  box-shadow: 0 12px 32px rgba(0,0,0,0.22);
}
.veo-tb-menu-item {
  display: block; width: 100%; text-align: left; border: 0; background: transparent;
  font: inherit; font-size: 15px; padding: 10px 14px; border-radius: 8px; cursor: pointer;
}
.veo-tb-menu-item:hover { background: #f5efe9; }
.veo-steps {
  height: ${CHROME_STEPS_H}px; box-sizing: border-box;
  background: #e7e1dc; border-bottom: 1px solid #d6cfc8;
  padding: 16px 28px 0; overflow-x: auto;
}
.veo-steps[hidden] { display: none; }
.veo-steps-list { display: flex; align-items: flex-start; gap: 20px; }
.veo-step { border: 0; background: transparent; padding: 0; cursor: pointer; font: inherit; display: flex; flex-direction: column; align-items: center; gap: 8px; flex: 0 0 auto; }
.veo-step-box {
  width: 116px; height: 64px; border-radius: 10px; background: #fff; border: 2px solid transparent;
  display: flex; align-items: center; justify-content: center; color: #2b2623;
  box-shadow: 0 1px 2px rgba(0,0,0,0.06);
}
.veo-step.is-active .veo-step-box { border-color: #b74d00; }
.veo-step-label { font-size: 13px; color: #6b635d; }
.veo-step.is-active .veo-step-label { color: #2b2623; font-weight: 600; }
.veo-thumb { width: 72px; height: 36px; border: 1.5px solid #c9c2bb; border-radius: 6px; padding: 6px 7px; box-sizing: border-box; display: flex; flex-direction: column; gap: 4px; }
.veo-thumb i { display: block; height: 3px; border-radius: 2px; background: #2b2623; width: 70%; }
.veo-thumb i + i { width: 45%; background: #c9c2bb; }
.veo-thumb b { display: block; height: 6px; width: 24px; border-radius: 2px; background: #b74d00; align-self: flex-end; }
.veo-thumb-inline { border-style: dashed; }
.veo-thumb-inline i { width: 100%; }
.veo-step-add { border: 0; background: transparent; font: inherit; font-size: 15px; color: #6b635d; cursor: pointer; display: inline-flex; align-items: center; gap: 8px; height: 64px; padding: 0 8px; }
.veo-step-add:hover { color: #2b2623; }
.veo-steps-toggle {
  position: absolute; left: 50%; top: var(--veo-chrome-h, ${CHROME_TOPBAR_H + CHROME_STEPS_H}px);
  transform: translate(-50%, -50%);
  border: 0; background: #2b2623; color: #fff; font: inherit; font-size: 14px; font-weight: 500;
  border-radius: 999px; padding: 10px 18px; cursor: pointer;
  display: inline-flex; align-items: center; gap: 8px;
  box-shadow: 0 6px 18px rgba(0,0,0,0.25);
}
`;
